import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '../../store/authStore';
import { Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { captureError } from '../../lib/errorHandler';

// Google branding-compliant SVG icon
// Source: https://developers.google.com/identity/branding-guidelines
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

// Apple logo SVG icon
function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
    </svg>
  );
}


/**
 * Detects user-initiated cancellation of the OAuth flow.
 * These errors are expected UX — silently swallow them.
 * Other errors (network, server) propagate to authStore.error for display.
 */
function isUserCancelledError(error: any): boolean {
  const code: string = error?.code ?? '';
  const message: string = (error?.message ?? '').toLowerCase();
  return (
    code === 'auth/popup-closed-by-user' ||
    code === 'auth/cancelled-popup-request' ||
    code === 'auth/user-cancelled' ||
    message.includes('cancelled') ||
    message.includes('canceled')
  );
}

interface SocialAuthButtonsProps {
  /** Route to navigate to after successful login for a returning user. Defaults to /app/map */
  redirectTo?: string;
}

/**
 * Sign in with Apple is offered on iOS only.
 *
 * iOS uses the native AuthenticationServices flow, which works as soon as the Apple
 * provider is enabled in Firebase. Android and web instead fall back to Apple's *web*
 * OAuth flow, which additionally needs a Services ID and a Sign in with Apple key
 * configured against the Firebase auth handler — credentials we deliberately do not
 * maintain (they are a second Apple key to rotate yearly).
 *
 * Rendering the button off iOS therefore guarantees the failure seen in CAPACITOR-36,
 * "The identity provider configuration is not found." App Store Guideline 4.8 only
 * requires Apple sign-in on iOS, and Android keeps Google plus phone OTP, so nothing
 * is lost by hiding it. If the web flow is ever configured, delete this constant.
 */
const APPLE_SIGN_IN_SUPPORTED = Capacitor.getPlatform() === 'ios';

/**
 * Shared Google + Apple sign-in buttons.
 * Import into both LoginScreen and RegisterScreen.
 *
 * Loading state is LOCAL to this component (loadingProvider) — not authStore.isLoading.
 * This prevents ProtectedLayout from rendering its full-screen spinner during social login.
 */
export function SocialAuthButtons({ redirectTo = '/app/map' }: SocialAuthButtonsProps) {
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'apple' | null>(null);
  const { socialLogin, clearError } = useAuthStore();
  const navigate = useNavigate();

  const isLoading = loadingProvider !== null;

  const handleSocialLogin = async (provider: 'google' | 'apple') => {
    clearError();
    setLoadingProvider(provider);
    try {
      const { isNew } = await socialLogin(provider);
      // Get fresh user state post-login
      const user = useAuthStore.getState().user;
      // New social user with no phone → collect phone before entering app
      if (isNew && !user?.phone) {
        navigate('/app/phone-collection', { replace: true });
      } else {
        navigate(redirectTo, { replace: true });
      }
    } catch (err: any) {
      // User cancellation is normal — don't show an error message
      if (isUserCancelledError(err)) {
        // Clear any error that socialLogin() may have set
        clearError();
      } else {
        captureError(err, 'socialLogin', { provider });
      }
      // For all other errors, authStore.error is already set by socialLogin()
      // The parent LoginScreen/RegisterScreen renders authStore.error
    } finally {
      setLoadingProvider(null);
    }
  };

  return (
    <div className="w-full max-w-sm space-y-3">
      {/* Side-by-side social buttons for compact layout */}
      <div className="flex gap-3">
        {/* Google Sign-In — white/outlined per Google branding guidelines */}
        <Button
          type="button"
          variant="outline"
          className="flex-1 h-11"
          disabled={isLoading}
          onClick={() => handleSocialLogin('google')}
        >
          {loadingProvider === 'google' ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <GoogleIcon className="w-5 h-5 mr-2" />
          )}
          Google
        </Button>

        {/* Apple Sign-In — dark button per Apple branding guidelines. iOS only; see
            APPLE_SIGN_IN_SUPPORTED. Google keeps flex-1 and simply fills the row
            when this is hidden. */}
        {APPLE_SIGN_IN_SUPPORTED && (
          <Button
            type="button"
            variant="outline"
            className="flex-1 h-11 bg-black text-white border-black hover:bg-black/90 hover:text-white"
            disabled={isLoading}
            onClick={() => handleSocialLogin('apple')}
          >
            {loadingProvider === 'apple' ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <AppleIcon className="w-5 h-5 mr-2" />
            )}
            Apple
          </Button>
        )}
      </div>
    </div>
  );
}
